import { useEffect, useState, useRef } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Camera, Upload, X, Loader2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { key: "carico", label: "Foto carico", cls: "bg-sky-600 hover:bg-sky-700" },
  { key: "scarico", label: "Foto scarico", cls: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "", label: "Altra foto", cls: "bg-slate-600 hover:bg-slate-700" },
];
const STAGE_BADGE = { carico: "bg-sky-600", scarico: "bg-emerald-600" };

function AuthImage({ jobId, photo, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let blobUrl;
    (async () => {
      try {
        const res = await api.get(`/jobs/${jobId}/photos/${photo.id}/download`, { responseType: "blob" });
        if (revoked) return;
        blobUrl = URL.createObjectURL(res.data);
        setUrl(blobUrl);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [jobId, photo.id]);

  if (!url)
    return (
      <div className="aspect-square w-full rounded-xl bg-slate-100 animate-pulse flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  return (
    <button type="button" onClick={() => onClick?.(url)} className="group relative block w-full">
      <img
        src={url}
        alt={photo.original_filename}
        className="aspect-square w-full rounded-xl object-cover border border-slate-200 group-hover:brightness-95 transition"
      />
    </button>
  );
}

export default function PhotoGallery({ job, canEdit, onChanged }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const stageRef = useRef("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const photos = job.photos || [];
  const has = (s) => photos.some((p) => p.stage === s && !p.is_deleted);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await api.post(`/jobs/${job.id}/photos`, form, {
          params: { stage: stageRef.current },
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} foto caricate`);
      // reload job
      const { data } = await api.get(`/jobs/${job.id}`);
      onChanged?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const isCompleted = job.status === "completato";
  const isAdmin = user?.role === "admin";

  const removePhoto = async (photoId) => {
    if (!confirm("Rimuovere questa foto?")) return;
    try {
      await api.delete(`/jobs/${job.id}/photos/${photoId}`);
      const { data } = await api.get(`/jobs/${job.id}`);
      onChanged?.(data);
      toast.success("Foto rimossa");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canUpload = canEdit && (isAdmin || !isCompleted);

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="photo-gallery">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
          <Camera className="w-4 h-4 text-sky-600" /> Foto del Lavoro
          <span className="text-xs font-mono text-slate-400">({photos.length})</span>
        </h3>
        {canUpload && (
          <input
            type="file"
            ref={fileRef}
            onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            accept="image/jpeg,image/png,image/webp,image/heic"
            multiple
            className="hidden"
            data-testid="photo-upload-input"
          />
        )}
      </div>
      {canUpload && (
        <div className="flex flex-wrap gap-2 mb-3">
          {STAGES.map((s) => (
            <button
              key={s.key || "altro"}
              type="button"
              onClick={() => { stageRef.current = s.key; fileRef.current?.click(); }}
              disabled={uploading}
              className={`inline-flex items-center gap-2 ${s.cls} text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-60 transition-colors`}
              data-testid={s.key ? `photo-upload-${s.key}-button` : "photo-upload-button"}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {s.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-3 text-xs font-semibold" data-testid="photo-requirements">
        {["carico", "scarico"].map((s) => (
          <span key={s} className={`inline-flex items-center gap-1 ${has(s) ? "text-emerald-700" : "text-rose-600"}`} data-testid={`photo-requirement-${s}`}>
            {has(s) ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
            Foto {s} {has(s) ? "caricata" : "obbligatoria per chiudere"}
          </span>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mb-4">
        Fotografa <b>oggetti aggiunti dal cliente non segnalati prima</b> così da documentarli e sommare l'importo nella sezione "Note di aggiunte".
      </p>

      {photos.length === 0 ? (
        <div className="text-sm text-slate-400 italic text-center py-6 border border-dashed border-slate-200 rounded-xl">
          Nessuna foto caricata.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
              <AuthImage jobId={job.id} photo={p} onClick={(u) => setPreview({ url: u, photo: p })} />
              {STAGE_BADGE[p.stage] && (
                <span className={`absolute top-1 left-1 ${STAGE_BADGE[p.stage]} text-white text-[10px] font-bold uppercase rounded-full px-2 py-0.5`} data-testid={`photo-stage-${p.id}`}>{p.stage}</span>
              )}
              <div className="mt-1.5 text-[10px] text-slate-500 truncate">{p.uploaded_by_name}</div>
              {(isAdmin || (user?.id === p.uploaded_by && !isCompleted)) && (
                <button
                  onClick={() => removePhoto(p.id)}
                  className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`delete-photo-${p.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="photo-preview-modal"
        >
          <img src={preview.url} alt="" className="max-h-[90vh] max-w-full rounded-xl object-contain" />
          <button
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 bg-white rounded-full p-2"
          >
            <X className="w-5 h-5 text-slate-900" />
          </button>
        </div>
      )}
    </section>
  );
}
